import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { baseApi } from './base-api';
import { API_ENDPOINTS } from '../utils/constants';

dayjs.extend(utc);
dayjs.extend(timezone);

interface InsightsResponse {
  type: string;
  response: any;
}

interface GetInsightsArgs {
  filters: string;
  include_testing?: boolean;
}

interface GetWrappedArgs {
  year: number;
}

// Interface for the data structure of a single knowledge gap
interface Gap {
  conversation_id: string;
  message_id: string;
  timestamp?: number;
  question?: string;
}

// Interface for the arguments of the updateKnowledgeGap mutation
interface UpdateKnowledgeGapArgs {
  gaps: Gap[];
  update_type: 'resolve' | 'mistake' | 'mark';
}

// Interface for the expected response from the update endpoint
interface UpdateKnowledgeGapResponse {
  type: 'success' | 'error';
  reason: string;
}

const insightsApi = baseApi.injectEndpoints({
  overrideExisting: import.meta.env.DEV,
  endpoints: (builder) => ({
    getInsights: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['Insights'],
    }),
    getInsightsLinksClicked: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/link-clicks', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
    }),
    getInsightsConversations: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/conversations', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsConversations'],
    }),
    getInsightsActions: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/actions', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsActions'],
    }),
    getInsightsRatings: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/ratings', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsRatings'],
    }),
    getInsightsRatingComments: builder.query<InsightsResponse, GetInsightsArgs & { score?: string; rating_type?: 'smiley' | 'nps' | 'csat' }>({
      query: ({ filters, score, rating_type, include_testing }) => {
        const url = new URL('/v1/insights/rating-comments', API_ENDPOINTS.KARLA_FLASK_API);
        const filtersWithScore = JSON.stringify({ ...JSON.parse(filters), include_testing, ...(score ? { score } : {}), ...(rating_type ? { rating_type } : {}) });
        url.searchParams.append('filters', filtersWithScore);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsRatings'],
    }),
    getInsightsResolutionRate: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/resolution-rate', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsResolutionRate'],
    }),
    getInsightsKnowledgeGaps: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/knowledge-gaps', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsKnowledgeGaps'],
    }),
    getInsightsLifetimeStats: builder.query<InsightsResponse, void>({
      query: () => ({
        url: new URL('/v1/insights/lifetime-stats', API_ENDPOINTS.KARLA_FLASK_API).toString(),
        method: 'GET',
      }),
      providesTags: ['InsightsLifetimeStats'],
    }),
    getWrapped: builder.query<InsightsResponse, GetWrappedArgs>({
      query: ({ year }) => {
        const url = new URL('/v1/insights/wrapped', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('year', String(year));
        url.searchParams.append('utc_offset', String(dayjs.utc().tz('Europe/Copenhagen').utcOffset() / 60));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['Wrapped'],
    }),
    getInsightsPeakTime: builder.query<InsightsResponse, GetInsightsArgs>({
      query: ({ filters, include_testing }) => {
        const url = new URL('/v1/insights/peak-time', API_ENDPOINTS.KARLA_FLASK_API);
        url.searchParams.append('filters', JSON.stringify({ ...JSON.parse(filters), include_testing }));
        url.searchParams.append('utc_offset', String(dayjs.utc().tz('Europe/Copenhagen').utcOffset() / 60));
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['InsightsPeakTime'],
    }),

    /**
     * Mutation to update one or more knowledge gaps.
     * This can be used to mark a gap as resolved or as a mistake.
     */
    updateKnowledgeGap: builder.mutation<UpdateKnowledgeGapResponse, UpdateKnowledgeGapArgs>({
      query: ({ gaps, update_type }) => ({
        url: new URL('/v1/insights/knowledge-gaps', API_ENDPOINTS.KARLA_FLASK_API).toString(),
        method: 'PATCH',
        body: {
          params: {
            gaps,
            type: update_type,
          },
        },
      }),
      /**
       * After a successful update, this invalidates the 'InsightsKnowledgeGaps' tag,
       * forcing a refetch of the getInsightsKnowledgeGaps query to get the latest data.
       */
      invalidatesTags: ['InsightsKnowledgeGaps'],
    }),
    downloadKnowledgeGaps: builder.mutation<Blob, { org_id: string; filters: object; output_type?: 'csv' | 'xlsx' }>({
      query: ({ filters, output_type = 'csv' }) => {
        const url = new URL('/v1/conversations/knowledge-gaps/download', API_ENDPOINTS.KARLA_FLASK_API);

        url.searchParams.append('params', JSON.stringify({ ...filters, output_type }));
        return {
          url: url.toString(),
          method: 'GET',
          responseHandler: (res: Response) => res.blob(),
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data: blob } = await queryFulfilled;

          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.setAttribute('download', `knowledge_gaps.${arg.output_type}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (e) {
          console.error('Download error', e);
        }
      },
    }),
  }),
});

export const {
  useDownloadKnowledgeGapsMutation,
  useGetInsightsLifetimeStatsQuery,
  useGetWrappedQuery,
  useGetInsightsQuery,
  useGetInsightsLinksClickedQuery,
  useGetInsightsActionsQuery,
  useGetInsightsConversationsQuery,
  useGetInsightsRatingsQuery,
  useGetInsightsRatingCommentsQuery,
  useGetInsightsResolutionRateQuery,
  useGetInsightsKnowledgeGapsQuery,
  useGetInsightsPeakTimeQuery,
  useUpdateKnowledgeGapMutation,
} = insightsApi;

export { insightsApi };
